/**
 * CoolifyTokenHealthCard (OCP-588) - is the Coolify panel still accepting the
 * API token OCP uses for every tenant operation?
 *
 * On 2026-08-15 the token died overnight (a Coolify 4.3.x upgrade purges API
 * tokens) and the first human signal was a maintenance window losing all four
 * tenant rolls. OCP now probes the token every 15 minutes and pushes an alert;
 * this card is where the operator sees the state, and the "Probe now" button
 * is the check after re-minting the token - and before scheduling a window,
 * which is why the card lives on the maintenance page.
 *
 * Self-fetching so the page's window list and this card fail independently.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

type TokenStatus = 'valid' | 'invalid' | 'unreachable';

interface TokenSample {
  observed_at: string;
  status: TokenStatus;
  outcome: string;
  http_status: number | null;
  latency_ms: number | null;
  panel_version: string | null;
  error: string | null;
}

interface TokenHealthResponse {
  configured: boolean;
  probe_url: string | null;
  panel_version: string | null;
  current: TokenSample | null;
  alert: {
    alerting: boolean;
    fail_streak: number;
    since: string | null;
    last_alerted_at: string | null;
    last_reason: string | null;
  };
  window: {
    hours: number;
    probes: number;
    valid: number;
    invalid: number;
    unreachable: number;
    valid_pct: number | null;
  };
  history: TokenSample[];
}

interface ProbeResponse {
  configured: boolean;
  observation: TokenSample | null;
  alerted: boolean;
  recovered: boolean;
  panel_updated: boolean;
}

const STRIP_SAMPLES = 96;

const STATUS_LABEL: Record<TokenStatus, string> = {
  valid: 'Token accepted',
  invalid: 'Token rejected',
  unreachable: 'Panel unreachable',
};

function statusClasses(status: TokenStatus): string {
  if (status === 'valid') {
    return 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-green-200 dark:ring-green-800';
  }
  if (status === 'invalid') {
    return 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-red-200 dark:ring-red-800';
  }
  return 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 ring-amber-200 dark:ring-amber-800';
}

function stripClasses(status: TokenStatus): string {
  if (status === 'valid') return 'bg-green-500/80';
  if (status === 'invalid') return 'bg-red-500';
  return 'bg-amber-500';
}

function formatTime(iso: string): string {
  return fmtDateTime(iso);
}

function relativeMinutes(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

export function CoolifyTokenHealthCard() {
  const [data, setData] = useState<TokenHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [probeNote, setProbeNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api<TokenHealthResponse>(`/v1/admin/coolify/token-health?limit=${STRIP_SAMPLES}`);
      setData(res);
    } catch (err) {
      setLoadError(
        err instanceof ApiError
          ? `Could not load the Coolify token state (HTTP ${err.status}).`
          : 'Could not load the Coolify token state.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const probeNow = useCallback(async () => {
    setProbing(true);
    setProbeNote(null);
    try {
      const res = await api<ProbeResponse>('/v1/admin/coolify/token-health/probe', { method: 'POST' });
      if (!res.configured) setProbeNote('Nothing probed - no panel URL/token configured.');
      else if (res.observation?.status === 'valid') setProbeNote(`Token accepted (panel ${res.observation.panel_version ?? 'unknown version'}).`);
      else setProbeNote(res.observation?.error ?? 'Probe failed.');
      await load();
    } catch (err) {
      setProbeNote(err instanceof ApiError ? `Probe failed (HTTP ${err.status}).` : 'Probe failed.');
    } finally {
      setProbing(false);
    }
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section
      data-testid="coolify-token-health-card"
      className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Coolify API token</h2>
          <p className="mt-1 max-w-2xl text-xs text-gray-500 dark:text-gray-400">
            Every tenant roll, env write and telemetry poll goes through this token. A Coolify upgrade
            has purged it before; orboto Control probes it every 15 minutes and alerts you when the panel
            rejects it. Check here before scheduling a window, and probe again after re-minting the token.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void probeNow()}
            disabled={probing || loading || (data !== null && !data.configured)}
            data-testid="coolify-token-probe-now"
            className="rounded border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 text-xs font-semibold text-blue-800 dark:text-blue-200 hover:bg-blue-100 disabled:opacity-50"
          >
            {probing ? 'Probing…' : 'Probe now'}
          </button>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {loadError && (
        <div
          role="alert"
          className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {loadError}
        </div>
      )}

      {probeNote && (
        <p data-testid="coolify-token-probe-note" className="mt-3 text-sm text-gray-700 dark:text-gray-300">
          {probeNote}
        </p>
      )}

      {!loadError && data && !data.configured && (
        <div className="mt-4 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          No Coolify panel URL/token configured (or COOLIFY_MOCK is on) - nothing is being probed.
        </div>
      )}

      {!loadError && data && data.configured && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {data.current ? (
              <span
                data-testid="coolify-token-status"
                className={`inline-flex items-center rounded px-2 py-1 text-xs font-semibold uppercase tracking-wide ring-1 ring-inset ${statusClasses(data.current.status)}`}
              >
                {STATUS_LABEL[data.current.status]}
              </span>
            ) : (
              <span className="inline-flex items-center rounded bg-gray-100 dark:bg-gray-800 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400 ring-1 ring-inset ring-gray-200 dark:ring-gray-700">
                No probe yet
              </span>
            )}
            {data.current && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                last probe {relativeMinutes(data.current.observed_at)}
                {data.current.latency_ms !== null ? ` · ${data.current.latency_ms} ms` : ''}
                {data.current.http_status !== null ? ` · HTTP ${data.current.http_status}` : ''}
              </span>
            )}
            {data.panel_version && (
              <span className="text-xs text-gray-500 dark:text-gray-400">panel {data.panel_version}</span>
            )}
            {data.probe_url && (
              <span className="font-mono text-xs text-gray-400 break-all">{data.probe_url}</span>
            )}
          </div>

          {data.current?.error && (
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{data.current.error}</p>
          )}

          {data.alert.alerting && (
            <div
              role="alert"
              className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
            >
              <span className="font-semibold">Alert open</span>
              {data.alert.since ? ` since ${formatTime(data.alert.since)}` : ''} ·{' '}
              {data.alert.fail_streak} failed probe(s) in a row. Do not schedule a maintenance window until
              this clears.
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label={`Accepted (${data.window.hours}h)`}>
              {data.window.valid_pct === null ? '-' : `${data.window.valid_pct}%`}
            </StatTile>
            <StatTile label="Probes">{data.window.probes}</StatTile>
            <StatTile label="Rejected">{data.window.invalid}</StatTile>
            <StatTile label="Unreachable">{data.window.unreachable}</StatTile>
          </div>

          {data.history.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Recent probes (oldest to newest)
              </div>
              <div className="mt-2 flex gap-[2px]" data-testid="coolify-token-strip">
                {[...data.history].reverse().map((s) => (
                  <span
                    key={s.observed_at}
                    title={`${formatTime(s.observed_at)} - ${STATUS_LABEL[s.status]}${s.error ? `: ${s.error}` : ''}`}
                    className={`h-6 flex-1 rounded-sm ${stripClasses(s.status)}`}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function StatTile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">{children}</div>
    </div>
  );
}
